import { Component, input, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TDropdown } from 'src/app/project/components/dropdown/dropdown.consts';
import { TPointsByWorkspace } from 'src/app/project/modules/points/points.model';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';

import { SiteOptionsColumnDropdownComponent } from './site-options-column-dropdown/site-options-column-dropdown.component';

import { SitePointFilterService } from 'src/app/project/modules/filters/site-point-filter.service';
import { PointsService } from 'src/app/project/modules/points/points.service';
import { PermissionsService } from 'src/app/project/modules/share/permissions.service';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { DropdownService } from '../../../components/dropdown/dropdown-service/dropdown.service';
import { ModalService } from '../../../components/modal/modal.service';
import { PointHalfModalService } from '../../points/point-half-modal/point-half-modal.service';
import { SiteDataService, TSiteData } from '../site-data.service';

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { PointExportModalComponent } from 'src/app/project/modules/exports/point-export-modal/point-export-modal.component';
import { canCreatePoint } from 'src/app/project/modules/share/share-utils/share-permissions';
import { EUserRole } from 'src/app/project/modules/share/share-utils/user-roles';
import { NEW_POINT_ID } from 'src/app/project/shared/constants/point.const';
import { EModule } from 'src/app/project/shared/enums/module.enum';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { ScreenService } from '../../../../core/services/window/screen.service';
import { EIconPath } from '../../../shared/enums/icons.enum';
import { checkCustomWorkspaceId } from '../../workspace/workspace';
import { EWorkspaces } from '../../workspace/workspaces.enum';
import { ResetTableService } from '../reset-table.service';
import { TColumn } from '../site-table/columns/column.model';
import { columnsChange$ } from '../site-table/columns/columns.store';
import { GET_GROUPING, groupingChange$ } from '../site-table/columns/grouping.store';
import { TableColumnsService } from '../site-table/columns/table-columns.service';
import { CustomTableService } from '../site-table/custom-table/custom-table.service';
import Table from '../site-table/custom-table/table/Table';
import { getHighlights } from './site-options';

@Component({
  selector: 'pp-site-options',
  templateUrl: './site-options.component.html',
  styleUrls: ['./site-options.component.scss'],
  standalone: false,
})
export class SiteOptionsComponent implements OnDestroy {
  @Input() ppDataFetched = false;
  @Input() ppIsOverview = false;
  ppIsTableLoading = input(false);

  private readonly destroy$ = new Subject<void>();
  private dropdown: TDropdown = this.dropdownService.getDropdown();
  site: TSiteData = this.siteDataService.getSite();

  workspaces$: Observable<TWorkspacesById>;
  workspace: TWorkspace;
  private points$: Observable<TPointsByWorkspace>;
  isMobileScreen = false;
  private isDesktopScreen = false;
  highlights = getHighlights();
  canExport = true;
  exportHidden = false;
  canCreatePoint = false;
  EUserRole = EUserRole;
  EWorkspaces = EWorkspaces;
  public sitePlanExists = false;
  tableColumnsHovered = false;
  exportHovered = false;
  columnsDropdownVisible = false;
  exportModalVisible = false;
  EIconPath = EIconPath;
  table: Table;
  hiddenColumnsCount = 0;
  tableGrouped = false;
  hasPoints = false;

  private cachedColumns: TColumn[];
  private workspaceId: string = null;

  constructor(
    private store: Store<{
      workspaces: TWorkspacesById;
      points: TPointsByWorkspace;
    }>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private dropdownService: DropdownService,
    private pointHalfModalService: PointHalfModalService,
    private siteDataService: SiteDataService,
    private activeService: ActiveService,
    private permissionsService: PermissionsService,
    private pointsService: PointsService,
    private screenService: ScreenService,
    private customTableService: CustomTableService,
    private tableColumnsService: TableColumnsService,
    private promptService: PromptService,
    private translationPipe: TranslationPipe,
    private resetTableService: ResetTableService,
    private sitePointFilterService: SitePointFilterService,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));
    this.points$ = this.store.pipe(select(EStore.POINTS));
  }

  ngOnInit() {
    this.screenService
      .observeOn$(['isMobile', 'isDesktop'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ isMobile, isDesktop }) => {
        this.isMobileScreen = isMobile;
        this.isDesktopScreen = isDesktop;
      });

    this.activatedRoute.paramMap.subscribe((params) => {
      this.workspaceId = params.get('id');
    });

    groupingChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const grouping = GET_GROUPING();
      this.tableGrouped = grouping.length > 0;
    });

    this.workspaces$.pipe(takeUntil(this.destroy$)).subscribe((workspaces) => {
      if (workspaces[this.workspaceId] && workspaces[this.workspaceId].loadedFullWorkspace) {
        this.sitePlanExists =
          typeof workspaces[this.workspaceId].sitePlan === 'undefined' ||
          !!workspaces[this.workspaceId]?.sitePlan;
        this.workspace = workspaces[this.workspaceId];
        this.canExport = this.permissionsService.areExportsEnabled(this.workspaceId);

        if (this.isDesktopScreen && !this.sitePlanExists) {
          this.site.sitePlanVisible = false;
        }

        this.canCreatePoint = canCreatePoint(this.workspace.share.shareOption);
      } else if (!this.workspaceId) {
        this.sitePlanExists = false;
      }
    });

    this.points$.pipe(takeUntil(this.destroy$)).subscribe((points) => {
      const workspaceId = this.activeService.getActiveWorkspaceId();
      this.table = this.customTableService.getTable();

      if (!workspaceId) {
        const selectedWorkspaces = this.pointsService.getOverviewSelectedWorkspaces();

        if (selectedWorkspaces.length > 0) {
          let exportsEnabled = false;

          selectedWorkspaces.forEach((iteratedWorkspaceId) => {
            if (this.permissionsService.areExportsEnabled(iteratedWorkspaceId)) {
              exportsEnabled = true;
            }
          });

          this.exportHidden = !exportsEnabled;
        } else {
          this.exportHidden = false;
        }

        this.hasPoints = !!Object.keys(points).length;
      } else {
        this.hasPoints = !!points[workspaceId]?.entities?.length;
      }
    });

    columnsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.hiddenColumnsCount = this.tableColumnsService
        .getColumns()
        .reduce((acc, column) => acc + (column.hidden ? 1 : 0), 0);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  resetTable(): void {
    const workspaceId = this.activeService.getActiveWorkspaceId();

    this.resetTableService.resetTable(workspaceId);
  }

  newPoint(): void {
    const workspaceId = this.activeService.getActiveWorkspaceId();

    this.pointHalfModalService.hideModal().then(() => {
      this.router.navigate(['/site', workspaceId, { outlets: { full: ['point', NEW_POINT_ID] } }]);
    });
  }

  export(): void {
    if (this.table?.getPoints().length === 0) {
      this.promptService.showWarning(this.translationPipe.transform('prompt_no_points_to_export'));
      return;
    }

    if (!this.exportHidden) {
      const workspaceId = this.activeService.getActiveWorkspaceId();

      this.exportModalVisible = true;

      this.modalService.setData({
        workspaceId,
        formats: {
          simpleList: true,
          detailedList: true,
          xlsxOutput: true,
          individualReports: true,
          wordPoints: true,
          sitePlan: true,
        },
      });

      this.modalService.showModal(PointExportModalComponent, {
        blur: true,
        props: {
          module:
            checkCustomWorkspaceId() === EWorkspaces.OVERVIEW ? EModule.overview : EModule.site,
        },
        onClose: () => {
          this.exportModalVisible = false;
        },
      });
    }
  }

  //
  // Column dropdown
  //

  toggleColumnDropdown(buttonId: string): void {
    if (!this.ppDataFetched) {
      return;
    }

    if (this.dropdown.visible && this.dropdown.buttonId === buttonId) {
      this.columnsDropdownVisible = false;
      this.dropdownService.hideDropdown();
    } else {
      this.columnsDropdownVisible = true;
      this.cachedColumns = cloneDeep(this.tableColumnsService.getColumns());

      this.dropdownService.showDropdown(buttonId, SiteOptionsColumnDropdownComponent, {
        suppressScrollbar: true,

        onClose: () => {
          this.columnsDropdownVisible = false;

          if (!isEqual(this.cachedColumns, this.tableColumnsService.getColumns())) {
            this.customTableService.reloadTable();
          }
        },
      });
    }
  }

  //
  // Site plan
  //

  toggleSitePlan(update: boolean): void {
    const sitePlanVisible = !this.site.sitePlanVisible;
    const table = this.customTableService.getTable();

    if (update) {
      this.siteDataService.setSitePlanVisibility({
        sitePlanFullWidth: this.site.sitePlanFullWidth,
        sitePlanVisible,
        workspaceId: this.workspaceId,
      });
    } else {
      this.siteDataService.setSitePlanVisibility({
        sitePlanFullWidth: this.site.sitePlanFullWidth,
        sitePlanVisible,
      });
    }

    this.siteDataService.sitePlanToggleEvent();

    this.sitePointFilterService.filterPoints({ _keepScrollPosition: true });

    if (table) {
      // user can toggle site plan when points are still loading
      table.tableBody.load(true);
    }
  }

  tableColumnsHover(): void {
    this.tableColumnsHovered = true;
  }

  tableColumnsHoverEnd(): void {
    this.tableColumnsHovered = false;
  }

  exportHover(): void {
    if (!this.exportHidden) {
      this.exportHovered = true;
    }
  }

  exportHoverEnd(): void {
    this.exportHovered = false;
  }
}
